/**
 * AssetDationForm — Sub-form for registering assets used as payment (dação de bens).
 * Renders conditional fields per asset_type.
 * Future: will call GET /collateral/assets to search existing assets.
 */
import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { AssetType, assetTypeLabels, AssetDationItem } from '../../../constants/payment';

interface Props {
    items: AssetDationItem[];
    onChange: (items: AssetDationItem[]) => void;
}

const emptyItem = (type: AssetType = AssetType.VEHICLE): AssetDationItem => ({
    asset_type: type,
    description: '',
    estimated_value: 0,
});

const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AssetDationForm: React.FC<Props> = ({ items, onChange }) => {
    const [expanded, setExpanded] = useState<number | null>(items.length === 0 ? null : 0);

    const addItem = () => {
        const newItems = [...items, emptyItem()];
        onChange(newItems);
        setExpanded(newItems.length - 1);
    };

    const removeItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
        setExpanded(null);
    };

    const updateItem = (index: number, field: string, value: any) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [field]: value };
        onChange(newItems);
    };

    const totalValue = items.reduce((sum, item) => sum + (item.estimated_value || 0), 0);

    return (
        <div className="space-y-3">
            <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bens ({items.length})
                    {totalValue > 0 && <span className="ml-2 text-green-600 font-normal">Total: {formatCurrency(totalValue)}</span>}
                </label>
                <button type="button" onClick={addItem}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600">
                    <FaPlus size={10} /> Adicionar bem
                </button>
            </div>

            {items.map((item, index) => (
                <div key={index} className="border dark:border-slate-600 rounded p-3 bg-gray-50 dark:bg-slate-700/50">
                    <div className="flex justify-between items-center mb-2">
                        <button type="button" onClick={() => setExpanded(expanded === index ? null : index)}
                            className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-500">
                            {assetTypeLabels[item.asset_type] || 'Bem'} {item.description && `— ${item.description.substring(0, 30)}`}
                            {item.estimated_value > 0 && ` (${formatCurrency(item.estimated_value)})`}
                        </button>
                        <button type="button" onClick={() => removeItem(index)}
                            className="text-red-400 hover:text-red-600"><FaTrash size={12} /></button>
                    </div>

                    {expanded === index && (
                        <div className="space-y-2 mt-2">
                            {/* Type selector */}
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de bem</label>
                                    <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                        value={item.asset_type} onChange={e => updateItem(index, 'asset_type', e.target.value)}>
                                        {Object.entries(assetTypeLabels).map(([k, v]) => (
                                            <option key={k} value={k}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Valor estimado (R$)</label>
                                    <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                        value={item.estimated_value || ''} onChange={e => updateItem(index, 'estimated_value', parseFloat(e.target.value) || 0)} />
                                </div>
                            </div>

                            {/* Description (all types) */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Descrição</label>
                                <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                    value={item.description} onChange={e => updateItem(index, 'description', e.target.value)}
                                    placeholder="Ex: Plantadeira Planticenter 2020" />
                            </div>

                            {/* VEHICLE fields */}
                            {item.asset_type === AssetType.VEHICLE && (
                                <div className="grid grid-cols-3 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Placa</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.plate || ''} onChange={e => updateItem(index, 'plate', e.target.value)} placeholder="ABC-1234" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Chassi</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.chassis || ''} onChange={e => updateItem(index, 'chassis', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Renavam</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.renavam || ''} onChange={e => updateItem(index, 'renavam', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Marca</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.brand || ''} onChange={e => updateItem(index, 'brand', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Modelo</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.model || ''} onChange={e => updateItem(index, 'model', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ano</label>
                                        <input type="number" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.year || ''} onChange={e => updateItem(index, 'year', parseInt(e.target.value) || undefined)} />
                                    </div>
                                </div>
                            )}

                            {/* MACHINERY fields */}
                            {item.asset_type === AssetType.MACHINERY && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Marca</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.brand || ''} onChange={e => updateItem(index, 'brand', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Modelo</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.model || ''} onChange={e => updateItem(index, 'model', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Nº Série</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.serial_number || ''} onChange={e => updateItem(index, 'serial_number', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Ano</label>
                                        <input type="number" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.year || ''} onChange={e => updateItem(index, 'year', parseInt(e.target.value) || undefined)} />
                                    </div>
                                </div>
                            )}

                            {/* REAL_ESTATE fields */}
                            {item.asset_type === AssetType.REAL_ESTATE && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Matrícula</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.registration_number || ''} onChange={e => updateItem(index, 'registration_number', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Cartório</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.registry_office || ''} onChange={e => updateItem(index, 'registry_office', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Área total (ha)</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.total_area || ''} onChange={e => updateItem(index, 'total_area', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Área edificada (m²)</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.built_area || ''} onChange={e => updateItem(index, 'built_area', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo</label>
                                        <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.land_type || 'RURAL'} onChange={e => updateItem(index, 'land_type', e.target.value)}>
                                            <option value="RURAL">Rural</option>
                                            <option value="URBAN">Urbano</option>
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* PRODUCT / GRAIN fields */}
                            {(item.asset_type === AssetType.PRODUCT || item.asset_type === AssetType.GRAIN) && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tipo de produto</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.product_type || ''} onChange={e => updateItem(index, 'product_type', e.target.value)}
                                            placeholder="Soja, Milho, Algodão..." />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Quantidade</label>
                                        <input type="number" step="0.01" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.quantity || ''} onChange={e => updateItem(index, 'quantity', parseFloat(e.target.value) || undefined)} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Unidade</label>
                                        <select className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.unit || 'TONELADAS'} onChange={e => updateItem(index, 'unit', e.target.value)}>
                                            <option value="TONELADAS">Toneladas</option>
                                            <option value="SACAS">Sacas</option>
                                            <option value="KG">Kg</option>
                                            <option value="LITROS">Litros</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Local de armazenamento</label>
                                        <input type="text" className="w-full border dark:border-slate-600 rounded px-2 py-1.5 text-sm dark:bg-slate-700 dark:text-white"
                                            value={item.storage_location || ''} onChange={e => updateItem(index, 'storage_location', e.target.value)} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}

            {items.length === 0 && (
                <p className="text-sm text-gray-400 italic text-center py-3">Nenhum bem adicionado. Clique em "+ Adicionar bem" acima.</p>
            )}

            {/* Future: search existing collateral assets */}
            {/* <button type="button" className="w-full text-xs text-blue-500 hover:text-blue-700 mt-2">
                🔍 Buscar assets no módulo de garantias (em breve)
            </button> */}
        </div>
    );
};

export default AssetDationForm;
